/**
 * Zero-elevation observability widget for AZAI staff.
 *
 * Rendered on the Dashboard when the caller is AZAI staff AND doesn't
 * have an active elevation for the currently-selected agency. Pulls
 * from ``GET /api/platform/summary`` — a purpose-built endpoint that
 * returns per-platform aggregates (tenant count, most-active agency,
 * last audit event) *without* touching tenant PHI.
 *
 * Design notes
 * ------------
 * * Fails gracefully — a 403 or network blip renders as a compact
 *   "unavailable" chip, never a toast.
 * * Refreshes on mount + when the caller elevates (elevation-changed
 *   event) so a supervisor who just returned from an elevation sees
 *   fresh counts.
 * * No PHI. Agency names shown are the tenant's registered business
 *   name — not patient / claim data.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Buildings, ChartLineUp, ClockClockwise, ShieldCheck } from '@phosphor-icons/react';
import api from '../../services/api';
import { logger } from '../../lib/logger';

const RelativeTime = ({ iso }) => {
  if (!iso) return <span className="text-slate-400">—</span>;
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  let label;
  if (mins < 1) label = 'just now';
  else if (mins < 60) label = `${mins} min ago`;
  else if (mins < 60 * 24) label = `${Math.floor(mins / 60)} h ago`;
  else label = `${Math.floor(mins / 1440)} d ago`;
  return (
    <span title={new Date(iso).toLocaleString()} className="text-slate-600">
      {label}
    </span>
  );
};

const StatBlock = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <div className="rounded-lg bg-indigo-100 p-2 shrink-0">
      <Icon size={18} className="text-indigo-800" weight="duotone" />
    </div>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 truncate">{value}</div>
      {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
    </div>
  </div>
);

export const PlatformSummaryWidget = () => {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const res = await api.get('/platform/summary');
      setState({ loading: false, error: null, data: res.data });
    } catch (err) {
      logger.debug('[platform-summary] fetch failed', {
        op: 'platform_summary_fetch',
        status: err?.response?.status,
      });
      setState({ loading: false, error: err?.response?.status || 'network', data: null });
    }
  }, []);

  useEffect(() => {
    load();
    const onElev = () => load();
    window.addEventListener('elevation-changed', onElev);
    return () => window.removeEventListener('elevation-changed', onElev);
  }, [load]);

  if (state.loading) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-lg p-4 animate-pulse"
        data-testid="platform-summary-loading"
      >
        <div className="h-3 bg-slate-100 rounded w-24 mb-3" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="h-12 bg-slate-100 rounded" />
          <div className="h-12 bg-slate-100 rounded" />
          <div className="h-12 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div
        className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-500"
        data-testid="platform-summary-unavailable"
      >
        Platform summary unavailable — {state.error === 403 ? 'restricted to AZAI staff' : 'try again later'}.
      </div>
    );
  }

  const { tenant_count, most_active_agency, last_audit_event } = state.data;
  const mostActiveValue = most_active_agency?.agency_name || 'No claims yet';
  const mostActiveSub = most_active_agency
    ? `${most_active_agency.claim_count_30d} claim${most_active_agency.claim_count_30d === 1 ? '' : 's'} · last 30 days`
    : '30 days';
  const auditValue = last_audit_event?.action
    ? last_audit_event.action.replace(/_/g, ' ')
    : 'None';
  const auditSub = last_audit_event ? (
    <>
      {last_audit_event.actor_email || 'system'} · <RelativeTime iso={last_audit_event.created_at} />
    </>
  ) : (
    'No activity yet'
  );

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4"
      data-testid="platform-summary-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-indigo-700" weight="duotone" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Platform activity (AZAI staff)
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatBlock
          icon={Buildings}
          label="Active tenants"
          value={tenant_count}
          sub="agencies live on AZAI"
          testid="platform-summary-tenant-count"
        />
        <StatBlock
          icon={ChartLineUp}
          label="Most active"
          value={mostActiveValue}
          sub={mostActiveSub}
          testid="platform-summary-most-active"
        />
        <StatBlock
          icon={ClockClockwise}
          label="Last audit event"
          value={auditValue}
          sub={auditSub}
          testid="platform-summary-last-audit"
        />
      </div>
      <p className="text-[11px] text-slate-400 mt-3">
        No PHI on this view — request platform elevation into a specific
        agency to see live billing data.
      </p>
    </div>
  );
};

export default PlatformSummaryWidget;
